// lib/api/apiClient.ts
'use client';

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';

import {
  clearStoredToken,
  getStoredToken,
  setStoredToken,
} from './tokenStorage';

/* -------------------------------------------------------------------------- */
/*                                   CONFIG                                   */
/* -------------------------------------------------------------------------- */

export const baseURL =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://127.0.0.1:8000/api/v1';

const RATE_LIMIT_CONFIG = {
  maxRequests: 5,
  timeWindow: 60_000, // 1 minute
};

/* -------------------------------------------------------------------------- */
/*                               RATE LIMITER                                 */
/* -------------------------------------------------------------------------- */

interface RateLimitEntry {
  count: number;
  resetAt: number;
}

class RateLimiter {
  private cache = new Map<string, RateLimitEntry>();

  canProceed(key: string): boolean {
    const now = Date.now();
    const entry = this.cache.get(key);

    if (!entry || now > entry.resetAt) {
      this.cache.set(key, {
        count: 1,
        resetAt: now + RATE_LIMIT_CONFIG.timeWindow,
      });
      return true;
    }

    if (entry.count < RATE_LIMIT_CONFIG.maxRequests) {
      entry.count++;
      return true;
    }

    return false;
  }

  waitTime(key: string): number {
    const entry = this.cache.get(key);
    return entry ? Math.max(0, entry.resetAt - Date.now()) : 0;
  }
}

/* -------------------------------------------------------------------------- */
/*                               REQUEST QUEUE                                */
/* -------------------------------------------------------------------------- */

interface QueueItem<T = unknown> {
  config: AxiosRequestConfig;
  resolve: (value: AxiosResponse<T> | PromiseLike<AxiosResponse<T>>) => void;
  reject: (reason?: any) => void;
}

class RequestQueue {
  private queue: QueueItem<any>[] = [];
  private processing = false;
  private limiter = new RateLimiter();

  add<T>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return new Promise((resolve, reject) => {
      this.queue.push({
        config,
        resolve: (value: AxiosResponse<any> | PromiseLike<AxiosResponse<any>>) => {
          resolve(value as AxiosResponse<T>);
        },
        reject,
      });
      if (!this.processing) this.process();
    });
  }

  private async process() {
    if (this.queue.length === 0) {
      this.processing = false;
      return;
    }

    this.processing = true;
    const item = this.queue[0];
    const key = item.config.url ?? 'unknown';

    if (!this.limiter.canProceed(key)) {
      setTimeout(() => this.process(), this.limiter.waitTime(key));
      return;
    }

    try {
      const response = await api.request(item.config);
      item.resolve(response);
    } catch (err) {
      item.reject(err);
    } finally {
      this.queue.shift();
      this.process();
    }
  }
}

/* -------------------------------------------------------------------------- */
/*                                 API CLIENT                                 */
/* -------------------------------------------------------------------------- */

class ApiClient {
  private axios: AxiosInstance;
  private queue = new RequestQueue();
  private refreshing = false;
  private subscribers: ((token: string) => void)[] = [];

  constructor() {
    this.axios = axios.create({
      baseURL,
      timeout: 30_000,
      withCredentials: true, // Required for HttpOnly cookies
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Client-Version': process.env.NEXT_PUBLIC_APP_VERSION ?? '1.0.0',
        'X-Client-Platform': 'web',
      },
    });

    this.setupInterceptors();
  }

  /* ------------------------------- INTERCEPTORS ------------------------------ */

  private setupInterceptors() {
    this.axios.interceptors.request.use(this.onRequest, Promise.reject);
    this.axios.interceptors.response.use(
      this.onResponse,
      this.onResponseError
    );
  }

  private onRequest = async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    config.headers['X-Request-ID'] = crypto.randomUUID();
    config.headers['X-Request-Timestamp'] = Date.now().toString();

    // Token is now handled automatically by the browser via HttpOnly cookies

    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 CLIENT API Request:', {
        url: config.url,
        method: config.method,
        data: this.redact(config.data),
      });
    }

    return config;
  };

  private onResponse = (response: AxiosResponse) => {
    // Auth tokens are now handled by the browser via Set-Cookie headers
    return response;
  };

  private onResponseError = async (error: AxiosError) => {
    const request = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !request._retry) {
      request._retry = true;
      try {
        return await this.refreshToken(request);
      } catch (refreshError) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth-error', { detail: { status: 401 } }));
        }
        throw this.normalizeError(refreshError as AxiosError);
      }
    }

    if (error.response?.status === 401 || error.response?.status === 403) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth-error', { detail: { status: error.response.status } }));
      }
    }

    throw this.normalizeError(error);
  };

  /* ------------------------------ TOKEN REFRESH ----------------------------- */

  private async refreshToken(request: InternalAxiosRequestConfig) {
    if (this.refreshing) {
      return new Promise(resolve => {
        this.subscribers.push(() => {
          resolve(this.axios(request));
        });
      });
    }

    this.refreshing = true;

    try {
      // Refresh request will send HttpOnly refresh_token cookie automatically
      await axios.post(
        `${baseURL}/auth/refresh`,
        {},
        { withCredentials: true }
      );

      this.subscribers.forEach(cb => cb(''));
      this.subscribers = [];

      return this.axios(request);
    } catch (err) {
      throw err;
    } finally {
      this.refreshing = false;
    }
  }

  /* ------------------------------ PUBLIC API -------------------------------- */

  request<T>(config: AxiosRequestConfig) {
    return this.axios.request<T>(config);
  }

  async get<T>(url: string, config?: AxiosRequestConfig) {
    const r = await this.queue.add<T>({ ...config, method: 'GET', url });
    return r.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    const r = await this.queue.add<T>({ ...config, method: 'POST', url, data });
    return r.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    const r = await this.queue.add<T>({ ...config, method: 'PUT', url, data });
    return r.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    const r = await this.queue.add<T>({ ...config, method: 'PATCH', url, data });
    return r.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig) {
    const r = await this.queue.add<T>({ ...config, method: 'DELETE', url });
    return r.data;
  }

  /* ------------------------------- UTILITIES -------------------------------- */

  private updateToken(auth: any) {
    // Handled by browser cookies
  }

  private redact(data: any) {
    if (!data || typeof data !== 'object') return data;
    const clone = { ...data };
    ['password', 'token', 'refresh_token'].forEach(k => {
      if (clone[k]) clone[k] = '***REDACTED***';
    });
    return clone;
  }

  private normalizeError(error: AxiosError) {
    return {
      status: error.response?.status ?? 0,
      message:
        (error.response?.data as any)?.message ??
        'Unexpected API error',
    };
  }
}

/* -------------------------------------------------------------------------- */
/*                                SINGLETON                                   */
/* -------------------------------------------------------------------------- */

export const apiClient = new ApiClient();
export const api = apiClient;
export default apiClient;